// "At a glance" — the org's counts from org_analytics (migration
// 20261022000008), shown on the Overview. COUNTS ONLY: how many children are
// linked and how many played in the last 30 days, never how any child is doing.
//
// The licence-months chart is plain SVG. Every bar carries its number as
// visible text and an accessible name, so nothing depends on colour.

import { useComfort } from "../ComfortContext";
import { fill, getDictionary, type Locale } from "../../i18n/dictionary";
import { getPortalData } from "../../data";
import type { OrgAnalytics } from "../../data/types";
import { useOrg } from "./OrgContext";
import { useOrgProfile } from "./orgProfile";
import { Card, ErrorPanel, GRID_TILES, formatMonth, formatNumber, useDemoVariant, useLoad } from "./ui";

type T = ReturnType<typeof getDictionary>["admin"]["analytics"];

function Row({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
      <dt style={{ fontSize: 14, color: "var(--ink-soft)", minWidth: 0 }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--ink-deep)" }}>{value}</dd>
    </div>
  );
}

export function LicenceMonthsChart({ months, locale, t }: { months: OrgAnalytics["licenceMonthsByMonth"]; locale: Locale; t: T }) {
  const W = 320;
  const H = 150;
  const top = 22;
  const bottom = 24;
  const max = Math.max(1, ...months.map((m) => m.licenceMonths));
  const slot = W / Math.max(months.length, 1);
  const barW = Math.min(34, slot * 0.6);
  return (
    <figure style={{ margin: 0 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: 520, display: "block" }} role="group" aria-label={t.chartTitle}>
        <line x1="0" x2={W} y1={H - bottom} y2={H - bottom} stroke="var(--line-strong)" strokeWidth="1" />
        {months.map((m, i) => {
          const h = ((H - top - bottom) * m.licenceMonths) / max;
          const x = i * slot + (slot - barW) / 2;
          const y = H - bottom - h;
          const label = fill(t.chartAria, { month: formatMonth(locale, m.month), n: formatNumber(locale, m.licenceMonths) });
          return (
            <g key={m.month} role="img" aria-label={label}>
              <title>{label}</title>
              <rect x={x} y={y} width={barW} height={Math.max(h, 1)} rx="4" fill="var(--brand)" />
              <text x={x + barW / 2} y={y - 6} textAnchor="middle" fontSize="11" fill="var(--ink-soft)" aria-hidden="true">
                {formatNumber(locale, m.licenceMonths, 1)}
              </text>
              <text x={x + barW / 2} y={H - 7} textAnchor="middle" fontSize="11" fill="var(--ink-faint)" aria-hidden="true">
                {formatMonth(locale, m.month, false)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption style={{ fontSize: 12.5, color: "var(--ink-faint)", marginTop: 6 }}>{t.chartNote}</figcaption>
    </figure>
  );
}

export function OrgAnalyticsPanel() {
  const { locale } = useComfort();
  const t = getDictionary(locale).admin.analytics;
  const { orgId } = useOrg();
  const variant = useDemoVariant();
  const profile = useOrgProfile();
  const load = useLoad(() => getPortalData().orgAnalytics(orgId), [orgId, variant]);

  return (
    <section aria-labelledby="glance-title" style={{ marginTop: 28 }}>
      <h2 id="glance-title" style={{ fontSize: 20, color: "var(--ink-deep)", margin: 0 }}>
        {t.title}
      </h2>
      <p style={{ margin: "4px 0 12px", fontSize: 14, color: "var(--ink-soft)" }}>{t.subtitle}</p>
      {load.loading && !load.data ? (
        // Not a live region: the screen above already announces its own load.
        <p style={{ color: "var(--ink-faint)", fontSize: 14, margin: "14px 0" }}>{t.loading}</p>
      ) : load.error || !load.data ? (
        <ErrorPanel title={t.errorTitle} message={getDictionary(locale).admin.licences.errGeneric} onRetry={load.reload} retryLabel={t.retry} />
      ) : (
        <AnalyticsBody a={load.data} t={t} locale={locale} solo={profile.kind === "individual" && load.data.members <= 1} />
      )}
    </section>
  );
}

function AnalyticsBody({ a, t, locale, solo }: { a: OrgAnalytics; t: T; locale: Locale; solo: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...GRID_TILES, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
        <Card title={t.people}>
          <p style={{ margin: 0, fontSize: 15, color: "var(--ink)" }}>
            {solo ? t.justYou : fill(t.membersLine, { members: a.members, admins: a.admins })}
          </p>
        </Card>
        <Card title={t.children}>
          <dl style={{ margin: 0 }}>
            <Row label={t.linked} value={a.childrenLinked} />
            <Row label={t.active30} value={a.childrenActive30d} />
            <Row label={t.inactive} value={a.childrenArchived} />
          </dl>
        </Card>
        <Card title={t.licences}>
          <dl style={{ margin: 0 }}>
            <Row label={t.live} value={a.licencesLive} />
            <Row label={t.claimed} value={a.licencesClaimed} />
            <Row label={t.endingSoon} value={a.licencesEnding30d} />
          </dl>
        </Card>
        <Card title={t.suggestions}>
          <dl style={{ margin: 0 }}>
            <Row label={t.sent} value={a.suggestions30d} />
            <Row label={t.seen} value={a.suggestionsSeen30d} />
            <Row label={t.played} value={a.suggestionsPlayed30d} />
          </dl>
        </Card>
      </div>
      {a.licenceMonthsByMonth.length > 0 && (
        <Card title={t.chartTitle}>
          <LicenceMonthsChart months={a.licenceMonthsByMonth} locale={locale} t={t} />
        </Card>
      )}
    </div>
  );
}
